import { MobXProviderContext } from 'mobx-react';
import type { InboundCallData } from 'Modules/softphone/components/SoftphoneCallBanner/index.types';
import { resolveContactInfo } from 'Modules/softphone/resolver/contactResolver';
import { isPhoneNumberString } from 'Modules/softphone/utils/validation';
import { useContext } from 'react';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';
import { CallBannerDisplay, DisplayConfig } from './index.types';

/**
 * Hook to determine what to display in the call banner
 *
 * Display logic:
 * 1. Has displayName: show name, "via {ringGroupName}" below the name when a
 *    ring group is also present, phone below, initials/avatar
 * 2. Has location (no name): show location, "via {ringGroupName}" when present,
 *    phone below, icon
 * 3. Has ringGroupName (no name/location): show ring group as the caller label,
 *    phone below, icon
 * 4. Has phoneNumber only: show phone, no phone below, icon
 * 5. No info: show "Unknown", no phone below, icon
 */
export function useCallBannerDisplay(data: InboundCallData): CallBannerDisplay {
  const { phoneNumber, displayName, location, ringGroupName, isVideo } = data;

  const rootStore = useContext(MobXProviderContext) as RootStoreProps;

  const personStore = rootStore?.person;
  const contactInfo = resolveContactInfo(personStore, phoneNumber, displayName);
  const avatarUrl = contactInfo.avatarUrl || data.avatarUrl;
  const formattedPhoneNumber = formatNumberWithNationalCode(phoneNumber);
  const isRingGroup = Boolean(ringGroupName);

  // Filter out location if it looks like a phone number (safety check)
  // SIP headers sometimes send phone number as display name
  const effectiveLocation = isPhoneNumberString(location)
    ? undefined
    : location;

  // Display configuration map - priority order matters
  // Simple avatar logic:
  // - If avatarUrl exists: show image, fallback to initials if image fails
  // - If no avatarUrl but has displayName: show initials
  // - If no displayName: show icon
  const displayConfig: Record<string, DisplayConfig> = {
    hasName: {
      condition: Boolean(displayName),
      callerLabel: displayName || '',
      showPhoneNumber: !isVideo,
      showInitials: !avatarUrl, // Not used by component, kept for consistency
      // Always pass displayName for initials (used as fallback if image fails)
      displayNameForAvatar: ringGroupName || displayName,
      // Ring group shown as a "via {ringGroupName}" line under the name
      ringGroupName: ringGroupName || undefined,
    },
    hasLocation: {
      condition: Boolean(effectiveLocation) && !displayName,
      callerLabel: effectiveLocation || '',
      showPhoneNumber: !isVideo,
      showInitials: false,
      displayNameForAvatar: undefined, // Never use location for initials
      // No contact match: SIP displayName becomes location — still show ring group
      ringGroupName: ringGroupName || undefined,
    },
    hasRingGroup: {
      condition: Boolean(ringGroupName) && !displayName,
      callerLabel: ringGroupName || '',
      showPhoneNumber: !isVideo,
      // If has ring group but no contact, show icon
      showInitials: false,
      displayNameForAvatar: undefined,
    },
    hasPhoneOnly: {
      condition:
        Boolean(phoneNumber) &&
        !displayName &&
        !effectiveLocation &&
        !ringGroupName,
      callerLabel: formattedPhoneNumber,
      showPhoneNumber: false,
      showInitials: false,
      displayNameForAvatar: undefined,
    },
    unknown: {
      condition: true,
      callerLabel: 'Unknown',
      showPhoneNumber: false,
      showInitials: false,
      displayNameForAvatar: undefined,
    },
  };

  const matchedConfig = Object.values(displayConfig).find(
    (cfg) => cfg.condition
  );

  const {
    callerLabel,
    showPhoneNumber,
    showInitials,
    displayNameForAvatar,
    ringGroupName: ringGroupSubLabel,
  } = matchedConfig;

  const callTypeLabel = isVideo ? 'Video call invite from' : 'Incoming call';

  return {
    callerLabel,
    callTypeLabel,
    showPhoneNumber,
    showInitials,
    isRingGroup,
    ringGroupName: ringGroupSubLabel,
    avatarUrl,
    displayNameForAvatar,
    formattedPhoneNumber,
  };
}
